import React, { useState, useMemo } from 'react';
import { Search, MessageSquare, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { SavChat, ChatStatus, ChatStatusColors } from '@/models/savChat';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

interface SavChatListProps {
  chats: SavChat[];
  selectedChatId: string | null;
  onSelectChat: (chat: SavChat) => void;
  stats: {
    total: number;
    open: number;
    inProgress: number;
    closed: number;
    unreadCount: number;
  };
}

export const SavChatList: React.FC<SavChatListProps> = ({
  chats,
  selectedChatId,
  onSelectChat,
  stats,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ChatStatus | 'all' | 'unread'>('all');

  // Filtrer les chats
  const filteredChats = useMemo(() => {
    return chats.filter((chat) => {
      // Filtre par statut
      if (statusFilter === 'unread') {
        // Filtrer uniquement les chats avec messages non lus
        if (chat.unreadByAdmin === 0) {
          return false;
        }
      } else if (statusFilter !== 'all' && chat.status !== statusFilter) {
        return false;
      }

      // Filtre par recherche (nom client, email, référence commande)
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          chat.customerName.toLowerCase().includes(search) ||
          chat.customerEmail.toLowerCase().includes(search) ||
          chat.orderReference.toLowerCase().includes(search)
        );
      }

      return true;
    });
  }, [chats, statusFilter, searchTerm]);

  const getStatusIcon = (status: ChatStatus) => {
    switch (status) {
      case ChatStatus.OPEN:
        return <AlertCircle className="h-4 w-4" />;
      case ChatStatus.IN_PROGRESS:
        return <Clock className="h-4 w-4" />;
      case ChatStatus.CLOSED:
        return <CheckCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full bg-surface overflow-hidden">
      {/* Header - fixe, pas de scroll */}
      <div className="pt-6 px-4 pb-4 border-b border-border-light flex-shrink-0">



        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-secondary" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('admin.savChat.admin.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder-foreground-secondary/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Filtres badges */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setStatusFilter('all')}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${statusFilter === 'all'
              ? 'bg-primary text-white'
              : 'bg-background text-foreground-secondary hover:bg-background-tertiary'
              }`}
          >
            {t('admin.savChat.admin.totalChats')} {stats.total}
          </button>

          <button
            onClick={() => setStatusFilter(ChatStatus.OPEN)}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${statusFilter === ChatStatus.OPEN
              ? 'bg-error text-white'
              : 'bg-error/10 text-error hover:bg-error/20'
              }`}
          >
            {t('admin.savChat.admin.openChats')} {stats.open}
          </button>

          <button
            onClick={() => setStatusFilter(ChatStatus.IN_PROGRESS)}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${statusFilter === ChatStatus.IN_PROGRESS
              ? 'bg-warning text-white'
              : 'bg-warning/10 text-warning-dark hover:bg-warning/20'
              }`}
          >
            {t('admin.savChat.admin.inProgressChats')} {stats.inProgress}
          </button>

          {stats.unreadCount > 0 && (
            <button
              onClick={() => setStatusFilter('unread')}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${statusFilter === 'unread'
                ? 'bg-error text-white'
                : 'bg-error/10 text-error hover:bg-error/20'
                }`}
            >
              {t('admin.savChat.admin.unreadMessages')} {stats.unreadCount}
            </button>
          )}
        </div>
      </div>

      {/* Chat list - scroll interne uniquement */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {filteredChats.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <MessageSquare className="h-12 w-12 text-foreground-secondary/50 mb-3" />
            <p className="text-foreground-secondary">
              {searchTerm || statusFilter !== 'all'
                ? t('admin.savChat.admin.noChatsFound')
                : t('admin.savChat.admin.noChatsAny')}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border-light">
            {filteredChats.map((chat) => (
              <button
                key={chat.id}
                onClick={() => onSelectChat(chat)}
                className={`w-full p-4 text-left hover:bg-background-secondary/50 transition-colors ${selectedChatId === chat.id ? 'bg-background-secondary' : ''
                  }`}
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground truncate">
                      {chat.customerName}
                    </h3>
                    <p className="text-xs text-foreground-secondary truncate">
                      {chat.customerEmail}
                    </p>
                  </div>
                  {chat.unreadByAdmin > 0 && (
                    <span className="flex-shrink-0 px-2 py-0.5 bg-error text-white text-xs font-medium rounded-full">
                      {chat.unreadByAdmin}
                    </span>
                  )}
                </div>

                {/* Order reference and reason */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-mono text-foreground-secondary">
                    #{chat.orderReference}
                  </span>
                  <span className="text-foreground-secondary">•</span>
                  <span className="text-xs text-foreground-secondary truncate">
                    {t(`admin.savChat.reasons.${chat.reason}`)}
                  </span>
                </div>

                {/* Last message preview */}
                <p className="text-xs text-foreground-secondary line-clamp-2 mb-2">
                  {chat.lastMessagePreview}
                </p>

                {/* Footer */}
                <div className="flex items-center justify-between gap-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${ChatStatusColors[chat.status].bg
                      } ${ChatStatusColors[chat.status].text}`}
                  >
                    {getStatusIcon(chat.status)}
                    {t(`admin.savChat.status.${chat.status.toLowerCase()}`)}
                  </span>
                  <span className="text-xs text-foreground-secondary">
                    {format(chat.lastMessageAt, 'dd MMM HH:mm', { locale: fr })}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

